import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { LogOut, Plus, Pencil, AlertTriangle, Inbox, BookOpen, Users, MailWarning, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, formatDate } from "@/lib/api";
import { useAuth, useSite } from "@/context/SiteContext";
import { Logo, Txt } from "@/components/site/Brand";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const STATUSES = ["New", "In Review", "Responded", "Closed"];
const blank = { title: "", slug: "", document_type: "", abstract: "", author_name: "", publication_date: "", publication_year: "", categories: [], keywords: "", related_service: "", status: "draft", file_name: "" };

function PubDialog({ open, onClose, initial, tax, onSaved }) {
  const { services } = useSite();
  const [v, setV] = useState(blank);
  const [errors, setErrors] = useState({});
  useEffect(() => {
    setErrors({});
    setV(initial ? { ...blank, ...initial, publication_year: initial.publication_year || "", keywords: (initial.keywords || []).join(", ") } : blank);
  }, [initial, open]);
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const body = { ...v, publication_year: v.publication_year ? Number(v.publication_year) : null, keywords: v.keywords.split(",").map((x) => x.trim()).filter(Boolean) };
    try {
      if (initial?.id) await api.put(`/admin/publications/${initial.id}`, body);
      else await api.post("/admin/publications", body);
      toast.success("Publication saved");
      onSaved();
      onClose();
    } catch (x) {
      const r = apiError(x);
      setErrors(r.errors);
      toast.error(r.message);
    }
  };
  const err = (k) => errors[k] && <p className="field-error" data-testid={`pub-${k}-error`}><AlertTriangle aria-hidden="true" className="h-4 w-4" />{errors[k]}</p>;
  const input = (k, label, props = {}) => (
    <div><label htmlFor={`pub-${k}`} className="field-label">{label}</label>
      <input id={`pub-${k}`} className="field-input" value={v[k]} onChange={set(k)} aria-invalid={errors[k] ? "true" : "false"} data-testid={`pub-${k}-input`} {...props} />{err(k)}</div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="publication-dialog">
        <DialogHeader><DialogTitle className="font-heading text-irid-navy">{initial?.id ? "Edit publication" : "New publication"}</DialogTitle>
          <DialogDescription>Title and document type are required (PUB-002). Only PDF files are permitted (PUB-008).</DialogDescription></DialogHeader>
        <form onSubmit={save} noValidate className="space-y-4">
          {input("title", "Title *")}
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label htmlFor="pub-document_type" className="field-label">Document type *</label>
              <select id="pub-document_type" className="field-input" value={v.document_type} onChange={set("document_type")} data-testid="pub-document_type-select">
                <option value="">Select…</option>{tax.document_types?.map((d) => <option key={d}>{d}</option>)}</select>{err("document_type")}</div>
            <div><label htmlFor="pub-status" className="field-label">Status</label>
              <select id="pub-status" className="field-input" value={v.status} onChange={set("status")} data-testid="pub-status-select">
                <option value="draft">Draft</option><option value="published">Published</option><option value="archived">Archived</option></select></div>
            {input("publication_year", "Publication year", { inputMode: "numeric", maxLength: 4 })}
            {input("publication_date", "Publication date (YYYY-MM-DD)")}
            {input("author_name", "Author")}
            <div><label htmlFor="pub-related_service" className="field-label">Related service</label>
              <select id="pub-related_service" className="field-input" value={v.related_service} onChange={set("related_service")} data-testid="pub-related_service-select">
                <option value="">None</option>{services.map((s) => <option key={s.slug} value={s.slug}>{s.title}</option>)}</select></div>
          </div>
          <div><label htmlFor="pub-abstract" className="field-label">Abstract</label>
            <textarea id="pub-abstract" rows={4} className="field-input" value={v.abstract} onChange={set("abstract")} data-testid="pub-abstract-input" /></div>
          <fieldset><legend className="field-label">Categories</legend>
            <div className="flex flex-wrap gap-3">{tax.publication_categories?.map((c) => (
              <label key={c} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={v.categories.includes(c)}
                onChange={(e) => setV({ ...v, categories: e.target.checked ? [...v.categories, c] : v.categories.filter((x) => x !== c) })} />{c}</label>))}</div></fieldset>
          {input("keywords", "Keywords (comma separated)")}
          {input("file_name", "File name (.pdf)")}
          <p className="text-xs text-irid-slate">PDF upload to storage is not enabled in this prototype; downloads serve a generated placeholder PDF.</p>
          <div className="flex justify-end gap-3 pt-2"><button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" data-testid="pub-save">Save</button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PublicationsTab() {
  const [rows, setRows] = useState([]);
  const [tax, setTax] = useState({});
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/publications").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/taxonomies").then((r) => setTax(r.data)); }, []);
  const badge = { draft: "bg-irid-cloud text-irid-ink", published: "bg-irid-green/15 text-irid-green-deep", archived: "bg-irid-gold/20 text-irid-orange-deep" };
  return (
    <div>
      <div className="mb-5 flex items-center justify-between"><h2 className="text-2xl">Publications</h2>
        <button type="button" className="btn-primary" onClick={() => { setEdit(null); setOpen(true); }} data-testid="admin-new-publication"><Plus aria-hidden="true" className="h-4 w-4" />New publication</button></div>
      <div className="overflow-x-auto rounded-xl border border-irid-cloud">
        <table className="table-irid" data-testid="admin-publications-table">
          <thead><tr><th>Title</th><th>Type</th><th>Year</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
          <tbody>{rows.map((p) => (
            <tr key={p.id} data-testid={`admin-pub-row-${p.slug}`}>
              <td className="max-w-md"><Txt>{p.title}</Txt></td><td>{p.document_type}</td><td>{p.publication_year}</td>
              <td><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${badge[p.status]}`}>{p.status}</span></td>
              <td className="whitespace-nowrap"><button type="button" className="inline-flex min-h-[44px] items-center gap-1 px-2 font-semibold text-irid-blue" onClick={() => { setEdit(p); setOpen(true); }} data-testid={`admin-edit-${p.slug}`}><Pencil aria-hidden="true" className="h-4 w-4" />Edit</button>
                {p.status !== "draft" && <Link to={`/publications/${p.slug}`} className="ml-2 inline-flex items-center gap-1 text-irid-blue" target="_blank"><ExternalLink aria-hidden="true" className="h-4 w-4" /><span className="sr-only">View</span></Link>}</td>
            </tr>))}</tbody>
        </table>
      </div>
      <PubDialog open={open} onClose={() => setOpen(false)} initial={edit} tax={tax} onSaved={load} />
    </div>
  );
}

function InquiriesTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [assignees, setAssignees] = useState([]);
  const load = () => api.get("/admin/inquiries", { params: filter ? { status: filter } : {} }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/admin/assignees").then((r) => setAssignees(r.data)); }, []);
  const patch = async (id, body) => {
    try { await api.patch(`/admin/inquiries/${id}`, body); toast.success("Inquiry updated"); load(); } catch (x) { toast.error(apiError(x).message); }
  };
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3"><h2 className="text-2xl">Inquiries</h2>
        <label className="flex items-center gap-2 text-sm font-semibold">Status
          <select className="field-input w-44" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="admin-inquiry-filter"><option value="">All</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></label></div>
      {rows.length === 0 ? <p className="rounded-xl border border-dashed border-irid-cloud p-8 text-irid-slate" data-testid="admin-inquiries-empty">No inquiries yet.</p> : (
        <div className="space-y-4" data-testid="admin-inquiries-list">{rows.map((q) => (
          <article key={q.id} className="rounded-xl border border-irid-cloud bg-white p-5" data-testid={`admin-inquiry-${q.reference}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div><p className="font-heading font-bold text-irid-navy">{q.name} <span className="font-body text-sm font-normal text-irid-slate">· {q.organization || "No organization"}</span></p>
                <p className="text-sm text-irid-slate">{q.reference} · {q.form_type} · {q.category} · {formatDate(q.submitted_at)}</p>
                <p className="text-sm"><a className="irid-link" href={`mailto:${q.email}`}>{q.email}</a>{q.telephone && ` · ${q.telephone}`}</p></div>
              <div className="flex flex-wrap gap-2">
                <select aria-label={`Status for ${q.reference}`} className="field-input w-40" value={q.status} onChange={(e) => patch(q.id, { status: e.target.value })} data-testid={`inquiry-status-${q.reference}`}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
                <select aria-label={`Assign ${q.reference}`} className="field-input w-48" value={q.assigned_user_id || ""} onChange={(e) => patch(q.id, { assigned_user_id: e.target.value })} data-testid={`inquiry-assign-${q.reference}`}>
                  <option value="">Unassigned</option>{assignees.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
              </div>
            </div>
            <p className="mt-4 whitespace-pre-wrap rounded-lg bg-irid-mist p-4 text-[15px]">{q.message}</p>
          </article>))}</div>)}
    </div>
  );
}

function NotificationsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/notification-log").then((r) => setRows(r.data)); }, []);
  return (
    <div>
      <h2 className="mb-2 text-2xl">Email notification log</h2>
      <p className="mb-5 text-irid-slate">Every inquiry alert attempt is recorded here (INQ-006). Failed alerts mean the inquiry is stored but staff were not emailed; review it in the Inquiries tab.</p>
      <div className="overflow-x-auto rounded-xl border border-irid-cloud"><table className="table-irid" data-testid="admin-notification-table">
        <thead><tr><th>When</th><th>Inquiry</th><th>Recipient</th><th>Status</th><th>Detail</th></tr></thead>
        <tbody>{rows.map((n) => <tr key={n.id}><td>{formatDate(n.at)}</td><td>{n.inquiry_reference}</td><td>{n.recipient || "—"}</td><td><span className={`font-semibold ${n.status === "sent" ? "text-irid-green-deep" : "text-irid-error"}`}>{n.status}</span></td><td>{n.detail}</td></tr>)}</tbody>
      </table></div>
    </div>
  );
}

function UsersTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setRows(r.data)); }, []);
  return (
    <div>
      <h2 className="mb-5 text-2xl">User accounts</h2>
      <div className="overflow-x-auto rounded-xl border border-irid-cloud"><table className="table-irid" data-testid="admin-users-table">
        <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Inquiry access</th></tr></thead>
        <tbody>{rows.map((u) => <tr key={u.id}><td>{u.name}</td><td>{u.email}</td><td className="capitalize">{u.role}</td><td>{u.can_view_inquiries ? "Authorized" : "No"}</td></tr>)}</tbody>
      </table></div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({});
  useEffect(() => { if (user) api.get("/admin/stats").then((r) => setStats(r.data)); }, [user]);
  if (user === null) return <div className="min-h-screen" aria-busy="true" />;
  if (!user) return <Navigate to="/admin/login" replace />;
  const isAdmin = user.role === "admin";
  const tabs = [
    { key: "publications", label: "Publications", Icon: BookOpen, show: true, El: PublicationsTab },
    { key: "inquiries", label: "Inquiries", Icon: Inbox, show: user.can_view_inquiries, El: InquiriesTab },
    { key: "notifications", label: "Notification log", Icon: MailWarning, show: isAdmin, El: NotificationsTab },
    { key: "users", label: "Users", Icon: Users, show: isAdmin, El: UsersTab },
  ].filter((t) => t.show);
  const cards = [["Publications", stats.publications], ["Published", stats.published_publications], ["Drafts", stats.draft_publications],
    ...(stats.new_inquiries !== undefined ? [["New inquiries", stats.new_inquiries]] : []), ...(stats.notification_issues !== undefined ? [["Email issues", stats.notification_issues]] : [])];
  return (
    <div className="min-h-screen bg-irid-mist" data-testid="admin-dashboard">
      <header className="on-dark bg-irid-navy text-white"><div className="growth-band" aria-hidden="true" />
        <div className="container-irid flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3"><span className="rounded-lg bg-white p-1.5"><Logo size={52} testid="admin-logo" /></span><span className="font-heading font-bold">IRID Admin</span></Link>
          <div className="flex items-center gap-4 text-sm"><span data-testid="admin-user-info">{user.name} · <span className="capitalize text-irid-gold">{user.role}</span></span>
            <button type="button" onClick={logout} className="btn-secondary-dark min-h-[40px] px-4 py-2 text-sm" data-testid="admin-logout"><LogOut aria-hidden="true" className="h-4 w-4" />Sign out</button></div>
        </div></header>
      <main id="main" className="container-irid py-10">
        <h1 className="text-3xl md:text-4xl">Dashboard</h1>
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-5" data-testid="admin-stats">
          {cards.map(([l, n]) => <div key={l} className="card-irid p-5"><p className="text-sm text-irid-slate">{l}</p><p className="font-heading text-3xl font-extrabold text-irid-navy">{n ?? "–"}</p></div>)}
        </div>
        {!user.can_view_inquiries && <p className="mt-6 rounded-lg border border-irid-blue/20 bg-white p-4 text-sm" data-testid="admin-editor-notice">Your Editor account manages content. Inquiry records, users and system settings need separate authorization.</p>}
        <Tabs defaultValue="publications" className="mt-10">
          <TabsList className="mb-6 flex h-auto flex-wrap justify-start gap-2 bg-transparent p-0">
            {tabs.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`admin-tab-${t.key}`} className="min-h-[44px] gap-2 rounded-lg border border-irid-cloud bg-white px-4 font-semibold text-irid-navy data-[state=active]:bg-irid-navy data-[state=active]:text-white"><t.Icon aria-hidden="true" className="h-4 w-4" />{t.label}</TabsTrigger>)}
          </TabsList>
          {tabs.map((t) => <TabsContent key={t.key} value={t.key} className="rounded-2xl bg-white p-6 md:p-8"><t.El /></TabsContent>)}
        </Tabs>
      </main>
    </div>
  );
}
